"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useState } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import { useLocale } from "next-intl";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";

import Link from "next/link";
import { FcGoogle } from "react-icons/fc";
import { cn } from "@/lib/utils";
import Password from "./password";
import { Button } from "./button";
import { Logo } from "./Logo";
import { signupAction } from "@/app/actions/auth";

const t = {
  en: {
    title: "Sign up for an account",
    signUpGoogle: "Sign up with Google",
    orEmail: "or continue with email",
    googleComplete: "Complete your Google sign-up",
    googleCompleteDesc: "Please set a password to complete your account registration",
    firstNameLabel: "First name",
    firstNamePlaceholder: "First name",
    lastNameLabel: "Last name",
    lastNamePlaceholder: "Last name",
    emailLabel: "Email address",
    emailLabelGoogle: "(from Google)",
    emailPlaceholder: "Enter your email",
    passwordLabel: "Password",
    passwordPlaceholder: "Create a password",
    confirmPasswordLabel: "Confirm password",
    confirmPasswordPlaceholder: "Confirm your password",
    submit: "Sign Up",
    submitting: "Creating account...",
    haveAccount: "Already have an account?",
    signIn: "Sign in",
    terms: "By clicking on sign up, you agree to our",
    termsLink: "Terms of Service",
    and: "and",
    privacyLink: "Privacy Policy",
    unexpectedError: "An unexpected error occurred",
    registrationFailed: "Registration failed",
  },
  de: {
    title: "Konto erstellen",
    signUpGoogle: "Mit Google registrieren",
    orEmail: "oder mit E-Mail fortfahren",
    googleComplete: "Google-Registrierung abschließen",
    googleCompleteDesc: "Bitte legen Sie ein Passwort fest, um die Kontoregistrierung abzuschließen",
    firstNameLabel: "Vorname",
    firstNamePlaceholder: "Vorname",
    lastNameLabel: "Nachname",
    lastNamePlaceholder: "Nachname",
    emailLabel: "E-Mail-Adresse",
    emailLabelGoogle: "(von Google)",
    emailPlaceholder: "E-Mail eingeben",
    passwordLabel: "Passwort",
    passwordPlaceholder: "Passwort erstellen",
    confirmPasswordLabel: "Passwort bestätigen",
    confirmPasswordPlaceholder: "Passwort bestätigen",
    submit: "Registrieren",
    submitting: "Konto wird erstellt...",
    haveAccount: "Bereits ein Konto?",
    signIn: "Anmelden",
    terms: "Mit dem Klick auf Registrieren stimmen Sie unseren",
    termsLink: "Nutzungsbedingungen",
    and: "und",
    privacyLink: "Datenschutzrichtlinien",
    unexpectedError: "Ein unerwarteter Fehler ist aufgetreten",
    registrationFailed: "Registrierung fehlgeschlagen",
  },
};

const formSchema = z
  .object({
    firstName: z
      .string({ required_error: "Please enter first name" })
      .min(1, "Please enter your first name"),
    lastName: z
      .string({ required_error: "Please enter last name" })
      .min(1, "Please enter your last name"),
    email: z
      .string({ required_error: "Please enter email" })
      .email("Please enter valid email")
      .min(1, "Please enter email"),
    password: z
      .string({ required_error: "Please enter password" })
      .min(8, "Password must be at least 8 characters")
      .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
      .regex(/[a-z]/, "Password must contain at least one lowercase letter")
      .regex(/[0-9]/, "Password must contain at least one number"),
    confirmPassword: z.string({ required_error: "Please confirm your password" }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

export type SignupUser = z.infer<typeof formSchema>;

export function SignupForm() {
  const locale = useLocale();
  const c = locale === "de" ? t.de : t.en;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();
  const searchParams = useSearchParams();
  const plan = searchParams.get("plan") as "monthly" | "yearly" | null;

  const isGoogleSignup = searchParams.get("google") === "true";
  const googleEmail = searchParams.get("email") || "";
  const googleFirstName = searchParams.get("firstName") || "";
  const googleLastName = searchParams.get("lastName") || "";
  const googleId = searchParams.get("googleId") || "";
  const googlePicture = searchParams.get("picture") || "";

  const form = useForm<SignupUser>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: googleFirstName,
      lastName: googleLastName,
      email: googleEmail,
      password: "",
      confirmPassword: "",
    },
  });

  async function onSubmit(values: SignupUser) {
    setIsLoading(true);
    setError(null);

    try {
      const result = await signupAction({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        password: values.password,
        plan: plan || undefined,
        googleId: isGoogleSignup ? googleId : undefined,
        googlePicture: isGoogleSignup ? googlePicture : undefined,
      });

      if (!result.success) {
        setError(result.error || c.registrationFailed);
        return;
      }

      if (result.redirectUrl) {
        window.location.href = result.redirectUrl;
        return;
      }

      if (result.requiresVerification && result.email) {
        const verifyUrl = new URL("/verify-email", window.location.origin);
        verifyUrl.searchParams.set("email", result.email);
        if (plan) verifyUrl.searchParams.set("plan", plan);
        router.push(verifyUrl.pathname + verifyUrl.search);
      }
    } catch (e) {
      setError(c.unexpectedError);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Form {...form}>
      <div className="flex items-center w-full justify-center px-4 py-12 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-md">
          <div>
            <div className="flex">
              <Logo />
            </div>
            <h2 className="mt-8 text-2xl font-bold leading-9 tracking-tight text-black dark:text-white">
              {c.title}
            </h2>
          </div>

          <div className="mt-10">
            <div>
              {!isGoogleSignup && (
                <>
                  <Button
                    as={Link}
                    href="/api/auth/google"
                    variant="outline"
                    className="w-full gap-3 mb-6"
                  >
                    <FcGoogle className="w-5 h-5" />
                    {c.signUpGoogle}
                  </Button>

                  <div className="relative mb-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-neutral-300 dark:border-neutral-700" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="bg-white dark:bg-neutral-950 px-2 text-neutral-500">
                        {c.orEmail}
                      </span>
                    </div>
                  </div>
                </>
              )}

              {isGoogleSignup && (
                <div className="mb-6 p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                  <div className="flex items-center gap-3">
                    <FcGoogle className="w-5 h-5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                        {c.googleComplete}
                      </p>
                      <p className="text-xs text-blue-600 dark:text-blue-400 mt-0.5">
                        {c.googleCompleteDesc}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {error && (
                <div className="mb-4 p-3 rounded-md bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <label htmlFor="firstName" className="block text-sm font-medium leading-6 text-neutral-700 dark:text-muted-dark">
                          {c.firstNameLabel}
                        </label>
                        <FormControl>
                          <div className="mt-2">
                            <input
                              id="firstName"
                              type="text"
                              placeholder={c.firstNamePlaceholder}
                              disabled={isLoading}
                              className="block w-full bg-white dark:bg-neutral-900 px-4 rounded-md border-0 py-1.5 shadow-aceternity text-black placeholder:text-gray-400 focus:ring-2 focus:ring-neutral-400 focus:outline-none sm:text-sm sm:leading-6 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                              {...field}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <label htmlFor="lastName" className="block text-sm font-medium leading-6 text-neutral-700 dark:text-muted-dark">
                          {c.lastNameLabel}
                        </label>
                        <FormControl>
                          <div className="mt-2">
                            <input
                              id="lastName"
                              type="text"
                              placeholder={c.lastNamePlaceholder}
                              disabled={isLoading}
                              className="block w-full bg-white dark:bg-neutral-900 px-4 rounded-md border-0 py-1.5 shadow-aceternity text-black placeholder:text-gray-400 focus:ring-2 focus:ring-neutral-400 focus:outline-none sm:text-sm sm:leading-6 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                              {...field}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div>
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <label htmlFor="email" className="block text-sm font-medium leading-6 text-neutral-700 dark:text-muted-dark">
                          {c.emailLabel}
                          {isGoogleSignup && (
                            <span className="ml-2 text-xs text-neutral-500">{c.emailLabelGoogle}</span>
                          )}
                        </label>
                        <FormControl>
                          <div className="mt-2">
                            <input
                              id="email"
                              type="email"
                              placeholder={c.emailPlaceholder}
                              disabled={isLoading}
                              readOnly={isGoogleSignup}
                              className={cn(
                                "block w-full bg-white dark:bg-neutral-900 px-4 rounded-md border-0 py-1.5 shadow-aceternity text-black placeholder:text-gray-400 focus:ring-2 focus:ring-neutral-400 focus:outline-none sm:text-sm sm:leading-6 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed",
                                isGoogleSignup && "bg-neutral-100 dark:bg-neutral-800 cursor-not-allowed"
                              )}
                              {...field}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div>
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <label htmlFor="password" className="block text-sm font-medium leading-6 text-neutral-700 dark:text-muted-dark">
                          {c.passwordLabel}
                        </label>
                        <FormControl>
                          <div className="mt-2">
                            <Password
                              id="password"
                              type="password"
                              placeholder={c.passwordPlaceholder}
                              disabled={isLoading}
                              className="block w-full bg-white dark:bg-neutral-900 px-4 rounded-md border-0 py-1.5 shadow-aceternity text-black placeholder:text-gray-400 focus:ring-2 focus:ring-neutral-400 focus:outline-none sm:text-sm sm:leading-6 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                              {...field}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div>
                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <label htmlFor="confirmPassword" className="block text-sm font-medium leading-6 text-neutral-700 dark:text-muted-dark">
                          {c.confirmPasswordLabel}
                        </label>
                        <FormControl>
                          <div className="mt-2">
                            <Password
                              id="confirmPassword"
                              type="password"
                              placeholder={c.confirmPasswordPlaceholder}
                              disabled={isLoading}
                              className="block w-full bg-white dark:bg-neutral-900 px-4 rounded-md border-0 py-1.5 shadow-aceternity text-black placeholder:text-gray-400 focus:ring-2 focus:ring-neutral-400 focus:outline-none sm:text-sm sm:leading-6 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                              {...field}
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div>
                  <Button className="w-full" disabled={isLoading}>
                    {isLoading ? c.submitting : c.submit}
                  </Button>
                  <p className={cn("text-sm text-neutral-500 text-center mt-4 text-muted dark:text-muted-dark")}>
                    {c.haveAccount}{" "}
                    <Link href="/login" className="text-black dark:text-white">
                      {c.signIn}
                    </Link>
                  </p>
                </div>
              </form>
            </div>

            <p className="text-neutral-600 dark:text-neutral-400 text-sm text-center mt-8">
              {c.terms}{" "}
              <Link href="#" className="text-neutral-500 dark:text-neutral-300">
                {c.termsLink}
              </Link>{" "}
              {c.and}{" "}
              <Link href="#" className="text-neutral-500 dark:text-neutral-300">
                {c.privacyLink}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </Form>
  );
}
